import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Input from "../../../../../../../components/Form/Input";
import InputRange from "../../../../../../../components/Form/InputRange";
import RadioGroup from "../../../../../../../components/Form/RadioGroup";
import Modal from "../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../context/StateContext";
import { ServiceBooking as Material } from "../../../../../../../models/service.booking";
import { Status } from "../../../../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import { formatCurrency } from "../../../../../../../util/prices";
import useStyles from "./styles";
import { AutoSuggest } from "Components/Form";
import { Room } from "Models/room";
import { useQuery } from "@tanstack/react-query";
import RoomService from "Services/room.service";

interface Props {
  material?: Material;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (material: Material) => void;
}

const MaterialModal = ({ material, open, hasError, isLoading, closeModal, submit }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [selectedMaterial, setSelectedMaterial] = useState<Material>();
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [locationList, setLocationList] = useState<Room[]>([]);

  const { isFetching: isFetchingRooms } = useQuery({
    queryKey: ["room.list"],
    queryFn: async () => {
      const response = await RoomService.getAll({
        status: Status.ACTIVE,
        jobId: "",
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        attributes: ["id", "name", "code"]
      });

      if (response && response.data && response.data.length > 0) {
        setLocationList(response.data);

        return response.data
      }

      return []
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
  });

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    volume: yup.string().when("isItem", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test("positive", translate("thisFieldIsRequired"), (volume) =>
          volume ? Number(volume) > 0 : false
        ),
    }),
    isItem: yup.boolean(),
  });

  const clearFields = () => {
    setSelectedMaterial(undefined);
    setStatus(Status.INACTIVE);
  };

  const handleValidateSuccess = (material?: Material) => {
    schema.validateSync(
      {
        name: material?.name,
        code: material?.code,
        volume: material?.volume,
        isItem: material?.isItem,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any) => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (material?: Material) => {
    try {
      return handleValidateSuccess(material);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid(selectedMaterial)) {
      if (selectedMaterial) submit({ ...selectedMaterial, status });
    }
  };

  const setValueFormattedForInput = (value?: string) => {
    const currency = currentUser?.company?.currency;

    if (!value) return formatCurrency(0, currency, false);

    return formatCurrency(value, currency, false);
  };

  const validatePrice = (value: string) => {
    if (/\d+[.|,]?$/g.test(value) || value === "") {
      const decimalSeparator = currentUser?.company?.currency?.decimalSeparator ?? ".";
      const decimalPoints = currentUser?.company?.currency?.decimalPoints ?? 2;

      if (value.includes(decimalSeparator)) {
        const isValid = value.split(decimalSeparator);

        if (isValid[1].length <= Number(decimalPoints)) {
          setSelectedMaterial((state) =>
            state ? { ...state, price: value } : ({ price: value } as Material)
          );
        }
      } else {
        setSelectedMaterial((state) =>
          state ? { ...state, price: value } : ({ price: value } as Material)
        );
      }
    }
  };

  useEffect(() => {
    if (material) {
      setSelectedMaterial(material);
      setStatus(material.status || "");
    }
  }, [material]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(selectedMaterial);
    }
  }, [selectedMaterial, hasFormBeenSubmitted]);

  return (
    <Modal
      open={open}
      handleClose={closeModal}
      title={material ? translate("editMaterial") : translate("createANewMaterial")}
      data-cy="materialModal"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        dataCySave: "submitButton",
        textSave: material ? translate("update") : translate("create"),
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("materialStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
      className={classes.modalWidth}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" justifyContent="space-between" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={selectedMaterial?.code || ""}
              onChange={(event) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, code: event.target.value }
                    : ({ code: event.target.value } as Material)
                )
              }
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={selectedMaterial?.name || ""}
              onChange={(event) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, name: event.target.value }
                    : ({ name: event.target.value } as Material)
                )
              }
            />
          </Grid>
        </Grid>
        <Grid container direction="row" justifyContent="space-between" spacing={3} className="mt-2">
          <Grid item md={6} lg={6}>
            <InputRange
              data-cy="materialVolume"
              name="materialVolume"
              label={translate("volume")}
              minValue={0}
              measurementInputRange={getMeasurement()}
              value={Number(selectedMaterial?.volume ?? 0)}
              onIncrease={(value) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, volume: Number(value) }
                    : ({ volume: Number(value) } as Material)
                )
              }
              onDecrease={(value) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, volume: Number(value) }
                    : ({ volume: Number(value) } as Material)
                )
              }
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue))
                  setSelectedMaterial((state) =>
                    state ? { ...state, volume: nValue } : ({ volume: nValue } as Material)
                  );
              }}
              error={!!getErrorByField("volume")}
              errorMessage={getErrorByField("volume")}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <InputRange
              data-cy="materialWeight"
              name="materialWeight"
              label={translate("weight")}
              minValue={0}
              measurementInputRange={getWeightMeasurement()}
              value={Number(selectedMaterial?.weight ?? 0)}
              onIncrease={(value) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, weight: Number(value) }
                    : ({ weight: Number(value) } as Material)
                )
              }
              onDecrease={(value) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, weight: Number(value) }
                    : ({ weight: Number(value) } as Material)
                )
              }
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue))
                  setSelectedMaterial((state) =>
                    state ? { ...state, weight: nValue } : ({ weight: nValue } as Material)
                  );
              }}
              error={!!getErrorByField("weight")}
              errorMessage={getErrorByField("weight")}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" justifyContent="space-between" spacing={3} className="mt-2">
          <Grid item md={6} lg={6}>
            <AutoSuggest
              disableCloseOnSelect
              disabled={isFetchingRooms}
              data-cy="autosuggestLocation"
              label={translate("location")}
              options={locationList}
              value={selectedMaterial?.rooms || ([] as Room[])}
              inputValue={""}
              getOptionLabel={(location: Room) => (location && location.name ? location.name : "")}
              getOptionSelected={(option: Room, value: Room) => option.id === value.id}
              multiple
              onChange={(selectedRooms: Room[]) => {
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, rooms: selectedRooms }
                    : ({ rooms: selectedRooms } as Material)
                )
              }}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              name="price"
              label={translate("price")}
              placeholder={translate("price")}
              error={!!getErrorByField("price")}
              errorMessage={getErrorByField("price")}
              value={selectedMaterial?.price}
              leftIcon={currentUser?.company?.currency?.symbol ?? "$"}
              onFocus={(event) =>
                setSelectedMaterial((state) =>
                  state
                    ? { ...state, price: String(setValueFormattedForInput(event.target.value)) }
                    : ({ price: String(setValueFormattedForInput(event.target.value)) } as Material)
                )
              }
              onChange={(event) => validatePrice(event.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" justifyContent="space-between" spacing={3} className="mt-2">
          <Grid item md={6} lg={6} container direction="column">
            <span className={classes.label}>{translate("isThisMaterialAnItem")}</span>
            <RadioGroup
              aria-label="material-is-item"
              name="radio-material-is-item"
              data-cy="radio-material-is-item"
              value={`${!!selectedMaterial?.isItem}`}
              onChange={(e) => {
                const isItem = !!(e.target.value === "true");

                setSelectedMaterial((state) =>
                  state ? { ...state, isItem } : ({ isItem } as Material)
                );
              }}
              options={[
                {
                  label: translate("yes"),
                  value: "true",
                  "data-cy": "radio-material-is-item-yes",
                },
                {
                  label: translate("no"),
                  value: "false",
                  "data-cy": "radio-material-is-item-no",
                },
              ]}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default MaterialModal;
